import { ColorDynamic } from '@superdispatch/ui';
import { ReactNode, RefObject } from 'react';
import { viewportHeight } from 'shared/layout/NavigationHeaderBannerHeight';
import styled, { css } from 'styled-components';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';

// viewportHeight() keeps the shell above the responsive web's fixed bottom bar;
// its CSS vars resolve to 0px in the mobile apps, so the height stays 100vh there.
// Scrolling is delegated to the inner Content component.
// Safe-area padding and dvh ensure the shell fills the screen correctly on Android 17 (edge-to-edge).
const nativeSafeAreaStyles = css`
  width: 100%;
  min-height: 100dvh;
  padding-left: env(safe-area-inset-left, 0px);
  padding-right: env(safe-area-inset-right, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
`;

const Container = styled.div<{ $isNative: boolean }>`
  display: flex;
  flex-direction: column;

  min-width: 100%;
  min-height: ${viewportHeight()};
  height: ${viewportHeight()};
  line-height: 1.42857142857;

  ${({ $isNative }) => $isNative && nativeSafeAreaStyles}
`;

const Content = styled.div<{ scrollable: boolean; bgcolor: ColorDynamic }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: ${({ scrollable }) => (scrollable ? 'auto' : 'visible')};
  -webkit-overflow-scrolling: touch;
  background-color: ${({ bgcolor }) => bgcolor};
`;

interface LayoutProps {
  header?: ReactNode;
  children: ReactNode;
  contentKey?: string;
  contentRef?: RefObject<HTMLDivElement>;
  scrollable?: boolean;
  backgroundColor?: ColorDynamic;
}

export function WebviewLayout({
  header,
  children,
  contentKey,
  contentRef,
  scrollable = true,
  backgroundColor = ColorDynamic.Silver200,
}: LayoutProps) {
  return (
    <Container $isNative={MobileAppBridge.isInjected()}>
      {header}

      <Content
        key={contentKey}
        ref={contentRef}
        data-cy="LoadboardLayoutContent"
        data-testid="webview-layout-content"
        scrollable={scrollable}
        bgcolor={backgroundColor}
      >
        {children}
      </Content>
    </Container>
  );
}
